import { SubformFieldDisplay } from "@fieldbook/common/components/SubformFieldDisplay"
import { formatSubformCell } from "@fieldbook/common/utils/subformFields"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import {
  ArrowLeft,
  MapPin,
  Navigation,
  PenBox,
  PlusIcon,
  Wand2,
} from "lucide-react"
import { Suspense, useCallback, useMemo } from "react"
import z from "zod"

import { getFeatureCoord } from "@/components/project/ProjectMap/utils"
import { useProjectData } from "@/contexts/ProjectData"
import { useDisplayDistance } from "@/hooks/useDisplayDistance"
import { usePhotosForEntity } from "@/hooks/usePhotosForEntity"
import {
  type SiteVegetationType,
  useSiteVegetationTypes,
} from "@/hooks/useSiteVegetationTypes"
import { useSurveySite } from "@/hooks/useSurveySite"

import { PhotoGrid } from "@/components/common/PhotoGrid"
import { SyncErrorBanner } from "@/components/common/SyncErrorBanner"
import { SyncStatusBadge } from "@/components/common/SyncStatusBadge"
import { ObservationTableRow } from "@/components/project/ProjectDataSheet/Observations/ObservationTableRow"
import { useObservationsWithDetails } from "@/components/project/ProjectDataSheet/Observations/useObservationsWithDetails"
import {
  CoordinateDisplay,
  NvisDescriptionFieldDisplay,
} from "@fieldbook/common/components"
import { getSurveySiteGeoJson } from "@fieldbook/common/utils"
import { Spinner } from "@fieldbook/ui/spinner"
import { useActiveNavTarget } from "@/hooks/useActiveNavTarget"
import { Button } from "@fieldbook/ui/button"
import { useAuth } from "@/hooks/useAuth"
import { ButtonLink } from "@fieldbook/ui/button-link"
import { Person } from "@fieldbook/common/types"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@fieldbook/ui/tabs"

const siteDetailSearchSchema = z.object({
  tab: z.enum(["data", "observations"]).optional(),
})

type SiteDetailTab = z.infer<typeof siteDetailSearchSchema>["tab"]

export const Route = createFileRoute(
  "/_private/projects/$id/data/sites/$siteId/",
)({
  component: SiteDetailRoute,
  validateSearch: (search) => siteDetailSearchSchema.parse(search),
})

function SiteDetailRoute() {
  const navigate = useNavigate()
  const { id: projectId } = Route.useParams()

  return (
    <div className="flex flex-col">
      <button
        onClick={() =>
          navigate({
            to: "/projects/$id/data/sites",
            params: { id: projectId },
          })
        }
        className="text-muted-foreground mb-4 mt-2 flex items-center gap-1 text-sm focus-visible:outline-0"
      >
        <ArrowLeft size={12} />
        <span>Survey Sites</span>
      </button>

      <SiteDetail />
    </div>
  )
}

function SiteDetail() {
  const { id: projectId, siteId } = Route.useParams()
  const { tab } = Route.useSearch()
  const { user, role } = useAuth()

  const { data: site, isPending: isSitePending } = useSurveySite({
    siteId,
    projectId,
  })
  const { data: vegetationTypeBySiteId = {} } =
    useSiteVegetationTypes(projectId)
  const vegetationType = vegetationTypeBySiteId[siteId]
  const canEdit = user?.id === site?.created_by || role === "Admin"

  const {
    data: { people, project, speciesRecorded, collections },
  } = useProjectData()

  const projectObservations = useObservationsWithDetails()
  const siteObservations =
    projectObservations?.filter((o) => o.survey_site_id === siteId) ?? []

  const { data: photos } = usePhotosForEntity({
    entityId: siteId,
    entityType: "survey_site",
  })

  const siteTypeLabel = project?.site_types?.find(
    ({ name }) => name === site?.type,
  )?.label

  // Get location coordinates based on location type
  const getLocationCoordinates = () => {
    if (!site) return null
    try {
      const geoJson = getSurveySiteGeoJson(site)
      const coords = getFeatureCoord(geoJson)
      return { latitude: coords[1], longitude: coords[0] }
    } catch {
      return null
    }
  }

  const location = getLocationCoordinates()

  const distanceKm = useDisplayDistance({
    latitude: location?.latitude ?? 0,
    longitude: location?.longitude ?? 0,
  })

  const [, setActiveNavTarget] = useActiveNavTarget()
  const navigate = useNavigate()
  const activeTab = tab ?? "data"

  const handleTabChange = useCallback(
    (nextTab: SiteDetailTab) => {
      navigate({
        to: "/projects/$id/data/sites/$siteId",
        params: { id: projectId, siteId },
        search: { tab: nextTab },
        replace: true,
      })
    },
    [navigate, projectId, siteId],
  )

  const handleNavigateTo = useCallback(() => {
    setActiveNavTarget({
      id: site?.id,
      type: "site",
    })
    navigate({ to: "/projects/$id", params: { id: projectId } })
  }, [navigate, projectId, setActiveNavTarget, site?.id])

  const handleEdit = useCallback(() => {
    navigate({
      to: "/projects/$id/sites/$siteId/edit",
      params: { id: projectId, siteId },
    })
  }, [navigate, projectId, siteId])

  const siteTypeDef = project?.site_types?.find(
    ({ name }) => name === site?.type,
  )

  const peopleMap = useMemo(() => {
    return (
      people?.reduce(
        (acc, person) => {
          acc[person.id] = person
          return acc
        },
        {} as Record<string, Person>,
      ) ?? {}
    )
  }, [people])

  const recorderNames =
    site?.recorders && site.recorders.length > 0
      ? site.recorders.map((id) => peopleMap[id]?.name ?? "Unknown").join(", ")
      : (user?.user_metadata?.name ?? "Unknown")

  if (isSitePending) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner />
      </div>
    )
  }

  if (!site) {
    return (
      <div className="py-8 text-center">
        <p className="text-muted-foreground">Survey site not found</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Site Header */}
      <div className="flex flex-col gap-1">
        <div className="flex w-full justify-between">
          <div className="flex w-full items-center justify-between gap-2">
            <span className="text-xl">{site.name}</span>
            <SyncStatusBadge entityId={siteId} />
          </div>
        </div>

        <div className="flex justify-end gap-1">
          {canEdit && (
            <ButtonLink
              className="items-center space-x-2"
              variant={"outline"}
              to="/projects/$id/sites/$siteId/edit"
              params={{ id: projectId, siteId }}
            >
              <PenBox className="size-4" />
              <span>Edit</span>
            </ButtonLink>
          )}
          <Button onClick={handleNavigateTo} className="items-center space-x-2">
            <Navigation className="size-4" />
            <span>Navigate</span>
          </Button>
        </div>
        {siteTypeLabel && (
          <span className="text-muted-foreground">{siteTypeLabel}</span>
        )}
      </div>

      {/* Sync Error Banner */}
      <SyncErrorBanner entityId={siteId} onEdit={handleEdit} />

      <Tabs
        value={activeTab}
        onValueChange={(value) => handleTabChange(value as SiteDetailTab)}
      >
        <TabsList className="bg-secondary-background w-full">
          <TabsTrigger value="data">Site data</TabsTrigger>
          <TabsTrigger value="observations">Observations</TabsTrigger>
        </TabsList>

        <TabsContent value="data" className="flex flex-col gap-4">
          {/* Photos */}
          {photos && photos.length > 0 && (
            <div className="flex flex-col gap-2">
              <span className="text-muted-foreground text-sm">Photos</span>
              <Suspense
                fallback={
                  <div className="grid grid-cols-3 gap-2">
                    {photos.map((p) => (
                      <div
                        key={p.id}
                        className="bg-muted aspect-square animate-pulse rounded"
                      />
                    ))}
                  </div>
                }
              >
                <PhotoGrid photoIds={photos.map((p) => p.id)} />
              </Suspense>
            </div>
          )}

          {/* Details Section */}
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-2 rounded border p-1">
              <div className="flex justify-between">
                {/* Recorders */}
                <DetailRow label="Recorders" value={recorderNames} />
                {/* Date */}
                <DetailRow
                  label="Date"
                  value={
                    site.created_at
                      ? new Date(site.created_at).toLocaleDateString(
                          undefined,
                          {
                            weekday: "short",
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          },
                        )
                      : "Unknown"
                  }
                />
                {/* Time */}
                <DetailRow
                  label="Time"
                  value={
                    site.created_at
                      ? new Date(site.created_at).toLocaleTimeString(
                          undefined,
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )
                      : "Unknown"
                  }
                />
              </div>
              <div className="flex justify-between">
                {/* Location */}
                {location && (
                  <DetailRow
                    label="Location"
                    value={
                      <div className="flex items-center gap-2">
                        <MapPin className="text-muted-foreground size-4" />
                        <span className="flex flex-col">
                          <CoordinateDisplay
                            value={location}
                            crs={project?.location_crs}
                          />
                          {distanceKm && (
                            <span className="text-muted-foreground text-sm">
                              {distanceKm} km away
                            </span>
                          )}
                        </span>
                      </div>
                    }
                  />
                )}
                {/* Location Type */}
                <DetailRow
                  label="Location Type"
                  value={formatLocationType(site.location_type)}
                />
              </div>
            </div>
            {vegetationType && (
              <div
                className="rounded border p-2"
                style={{
                  borderColor: vegetationType.display_colour ?? undefined,
                }}
              >
                <DetailRow
                  label="Vegetation Type"
                  value={
                    <VegetationTypeBadge vegetationType={vegetationType} />
                  }
                />
              </div>
            )}

            {/* Description */}
            {site.description && (
              <div className="flex flex-col gap-1">
                <span className="text-muted-foreground text-sm">
                  Description
                </span>
                <p className="text-foreground whitespace-pre-wrap">
                  {site.description}
                </p>
              </div>
            )}

            {/* Notes */}
            {site.notes && (
              <div className="flex flex-col gap-1">
                <span className="text-muted-foreground text-sm">Notes</span>
                <p className="text-foreground whitespace-pre-wrap">
                  {site.notes}
                </p>
              </div>
            )}

            {/* Custom Fields */}
            {(() => {
              const valuesByName = new Map(
                (site.custom_fields ?? []).map((f) => [f.name, f.value]),
              )
              // Render from the site type definition so empty NVIS fields
              // still appear (they get a "Generate description" entry point
              // when the site has observations and the user can edit).
              const fieldDefs = siteTypeDef?.custom_fields ?? []
              if (fieldDefs.length === 0) return null
              const rows = fieldDefs.map((def) => {
                const value = valuesByName.get(def.name)
                const label = def.label ?? formatFieldLabel(def.name)
                const hasValue =
                  value !== null && value !== undefined && value !== ""

                if (def.type === "nvis_vegetation_description") {
                  const canAutoGenerate =
                    !hasValue && canEdit && (siteObservations?.length ?? 0) > 0
                  if (!hasValue && !canAutoGenerate) return null
                  return (
                    <div
                      key={def.name}
                      className="flex flex-col gap-1 border-b pb-2 last:border-b-0"
                    >
                      <span className="text-muted-foreground text-sm">
                        {label}
                      </span>
                      {hasValue ? (
                        <NvisDescriptionFieldDisplay
                          value={value}
                          species={speciesRecorded ?? []}
                          collections={collections ?? []}
                        />
                      ) : (
                        <Button
                          className="border-primary text-primary items-center gap-2 border"
                          variant="outline"
                          onClick={() =>
                            navigate({
                              to: "/projects/$id/sites/$siteId/edit",
                              params: { id: projectId, siteId },
                              search: { autoGenerateNvis: def.name },
                            })
                          }
                        >
                          <Wand2 className="size-4" />
                          Generate vegetation description
                        </Button>
                      )}
                    </div>
                  )
                }

                if (def.type === "subform") {
                  return (
                    <div
                      key={def.name}
                      className="space-y-2 border-b pb-2 last:border-b-0"
                    >
                      <span className="text-muted-foreground text-sm">
                        {label}
                      </span>
                      <SubformFieldDisplay
                        field={def}
                        value={value}
                        renderValue={(child, childValue) =>
                          child.type === "nvis_vegetation_description" ? (
                            <NvisDescriptionFieldDisplay
                              value={childValue}
                              species={speciesRecorded ?? []}
                              collections={collections ?? []}
                            />
                          ) : (
                            formatSubformCell(childValue)
                          )
                        }
                      />
                    </div>
                  )
                }
                if (!hasValue) return null
                const displayValue =
                  value instanceof Date
                    ? value.toLocaleDateString()
                    : Array.isArray(value)
                      ? value.join(", ")
                      : String(value)
                return (
                  <DetailRow
                    key={def.name}
                    label={label}
                    value={displayValue}
                  />
                )
              })
              const visibleRows = rows.filter((r) => r !== null)
              if (visibleRows.length === 0) return null
              return (
                <div className="flex flex-col gap-2">
                  <span className="text-muted-foreground text-sm">
                    Additional Fields
                  </span>
                  <div className="flex flex-col gap-2">{visibleRows}</div>
                </div>
              )
            })()}
          </div>
        </TabsContent>

        <TabsContent value="observations" className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground text-sm">
              {siteObservations.length} observation
              {siteObservations.length !== 1 ? "s" : ""}
            </span>
            <ButtonLink
              to="/projects/$id/observations/new"
              params={{ id: projectId }}
              search={{ siteId: site.id }}
              className="flex items-center"
            >
              <PlusIcon className="size-4" />
              <span>Add</span>
            </ButtonLink>
          </div>
          {siteObservations.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              No observations recorded
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {siteObservations.map((obs) => (
                <ObservationTableRow key={obs.id} obs={obs} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}

function VegetationTypeBadge({
  vegetationType,
}: {
  vegetationType: SiteVegetationType
}) {
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5">
      <span
        className="size-2.5 shrink-0 rounded-sm border"
        style={{ backgroundColor: vegetationType.display_colour ?? "#2563eb" }}
      />
      <span className="truncate">{vegetationType.name}</span>
    </span>
  )
}

function DetailRow({
  label,
  value,
}: {
  label: string
  value: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-muted-foreground text-sm">{label}</span>
      <div className="text-foreground">{value}</div>
    </div>
  )
}

function formatFieldLabel(key: string): string {
  return key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
}

function formatLocationType(type: "point" | "line" | "polygon"): string {
  switch (type) {
    case "point":
      return "Point"
    case "line":
      return "Line / Transect"
    case "polygon":
      return "Polygon / Area"
    default:
      return type
  }
}
